import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Calendar, MessageSquare, Check, Pencil, Trash2, X, Save, Eraser } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface MailingHistory {
  id: string;
  campaign_name: string | null;
  mailed_date: string | null;
  mailing_round: number;
  letter_type: string | null;
  returned: boolean;
  response_received: boolean;
  response_date: string | null;
  response_notes: string | null;
}

interface Props {
  history: MailingHistory[];
  onResponseSaved: () => void;
  recordId?: string;
}

export function MailingHistoryTimeline({ history, onResponseSaved, recordId }: Props) {
  const [respondingTo, setRespondingTo] = useState<string | null>(null);
  const [responseDate, setResponseDate] = useState("");
  const [responseNotes, setResponseNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ campaign_name: "", mailed_date: "", letter_type: "", returned: false });

  const handleSaveResponse = async (historyId: string) => {
    setSaving(true);
    const { error } = await supabase
      .from("mailing_history")
      .update({
        response_received: true,
        response_date: responseDate || null,
        response_notes: responseNotes || null,
      })
      .eq("id", historyId);

    setSaving(false);
    if (error) {
      toast.error("Failed to save response");
    } else {
      // Auto-update mailing_records status to "responded" and set response date
      if (recordId) {
        await supabase.from("mailing_records").update({ 
          mailing_status: "responded", 
          response: responseDate || new Date().toISOString().split("T")[0] 
        }).eq("id", recordId);
      }
      toast.success("Response recorded");
      setRespondingTo(null);
      setResponseDate("");
      setResponseNotes("");
      onResponseSaved();
    }
  };

  const openResponseForm = (h: MailingHistory) => {
    setRespondingTo(h.id);
    setEditingId(null);
    setResponseDate(h.response_date || new Date().toISOString().split("T")[0]);
    setResponseNotes(h.response_notes || "");
  };

  const startEditing = (h: MailingHistory) => {
    setEditingId(h.id);
    setRespondingTo(null);
    setEditForm({
      campaign_name: h.campaign_name || "",
      mailed_date: h.mailed_date || "",
      letter_type: h.letter_type || "",
      returned: h.returned,
    });
  };

  const handleSaveEdit = async (historyId: string) => {
    setSaving(true);
    const { error } = await supabase
      .from("mailing_history")
      .update({
        campaign_name: editForm.campaign_name || null,
        mailed_date: editForm.mailed_date || null,
        letter_type: editForm.letter_type || null,
        returned: editForm.returned,
      })
      .eq("id", historyId);

    setSaving(false);
    if (error) {
      toast.error("Failed to update");
    } else {
      toast.success("Mailing updated");
      setEditingId(null);
      onResponseSaved();
    }
  };

  // Recalculate times_mailed and last_mailed_date on the parent mailing_records row
  const recalculateMailingStats = async () => {
    if (!recordId) return;
    
    // Count history entries that have a non-null mailed_date (actually sent)
    const { data: sentHistory } = await supabase
      .from("mailing_history")
      .select("mailed_date")
      .eq("mailing_record_id", recordId)
      .not("mailed_date", "is", null)
      .order("mailed_date", { ascending: false });

    const newTimesMailed = sentHistory?.length || 0;
    const newLastMailedDate = sentHistory?.[0]?.mailed_date || null;

    await supabase
      .from("mailing_records")
      .update({
        times_mailed: newTimesMailed,
        last_mailed_date: newLastMailedDate,
      })
      .eq("id", recordId);
  };

  const handleDelete = async (historyId: string, mailingRecordId?: string) => {
    const { error } = await supabase
      .from("mailing_history")
      .delete()
      .eq("id", historyId);

    if (error) {
      toast.error("Failed to delete");
    } else {
      toast.success("Mailing entry deleted");
      await recalculateMailingStats();
      onResponseSaved();
    }
  };

  const handleCorrector = async (historyId: string) => {
    setSaving(true);
    const { error } = await supabase
      .from("mailing_history")
      .update({
        campaign_name: null,
        mailed_date: null,
        response_received: false,
        response_date: null,
        response_notes: null,
      })
      .eq("id", historyId);

    setSaving(false);
    if (error) {
      toast.error("Failed to clear mailing data");
    } else {
      toast.success("Mailing entry cleared — marked as never sent");
      await recalculateMailingStats();
      onResponseSaved();
    }
  };

  return (
    <div>
      <h4 className="font-medium mb-3 flex items-center gap-2">
        <Calendar className="h-4 w-4" />
        Mailing History
      </h4>
      {history.length === 0 ? (
        <p className="text-sm text-muted-foreground">No mailings recorded</p>
      ) : (
        <div className="space-y-3">
          {history.map((h) => (
            <div key={h.id} className="p-3 bg-muted/50 rounded-lg space-y-2">
              {editingId === h.id ? (
                /* Edit form */
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Campaign Name</p>
                      <Input
                        value={editForm.campaign_name}
                        onChange={(e) => setEditForm(f => ({ ...f, campaign_name: e.target.value }))}
                        className="h-8 text-sm"
                        placeholder="Campaign name"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Mailed Date</p>
                      <Input
                        type="date"
                        value={editForm.mailed_date}
                        onChange={(e) => setEditForm(f => ({ ...f, mailed_date: e.target.value }))}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Letter Type</p>
                      <Input
                        value={editForm.letter_type}
                        onChange={(e) => setEditForm(f => ({ ...f, letter_type: e.target.value }))}
                        className="h-8 text-sm"
                        placeholder="Letter type"
                      />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.returned}
                          onChange={(e) => setEditForm(f => ({ ...f, returned: e.target.checked }))}
                        />
                        Returned
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs" onClick={() => handleSaveEdit(h.id)} disabled={saving}>
                      <Save className="h-3 w-3 mr-1" /> {saving ? "Saving..." : "Save"}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingId(null)}>
                      <X className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                /* Display mode */
                <>
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                      {h.mailing_round}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{h.campaign_name || "—"}</p>
                      <p className="text-xs text-muted-foreground">
                        {h.mailed_date || "No date"} · {h.letter_type || "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {h.returned && <Badge variant="destructive">Returned</Badge>}
                      {h.response_received && (
                        <Badge variant="default" className="cursor-pointer" onClick={() => openResponseForm(h)}>
                          <Check className="h-3 w-3 mr-1" /> {h.response_date || "Response"}
                        </Badge>
                      )}
                      {!h.response_received && !h.returned && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openResponseForm(h)}>
                          <MessageSquare className="h-3 w-3 mr-1" /> Log Response
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-orange-600 hover:text-orange-700" onClick={() => handleCorrector(h.id)} disabled={saving} title="Clear mailing data (mark as never sent)">
                          <Eraser className="h-3 w-3 mr-1" /> Corrector
                        </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEditing(h)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete mailing entry?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently remove round {h.mailing_round} ({h.campaign_name || "unnamed"}) from the history.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(h.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  {h.response_received && h.response_notes && respondingTo !== h.id && (
                    <p className="text-xs text-muted-foreground ml-11 italic">"{h.response_notes}"</p>
                  )}

                  {respondingTo === h.id && (
                    <div className="ml-11 space-y-2 pt-1 border-t border-border/50">
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground mb-1">Response Date</p>
                          <Input type="date" value={responseDate} onChange={(e) => setResponseDate(e.target.value)} className="h-8 text-sm" />
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Notes</p>
                        <Textarea value={responseNotes} onChange={(e) => setResponseNotes(e.target.value)} placeholder="What did they say?" className="text-sm min-h-[50px]" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="h-7 text-xs" onClick={() => handleSaveResponse(h.id)} disabled={saving}>
                          {saving ? "Saving..." : "Save Response"}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setRespondingTo(null)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
